import { FIXED_RELEASE_BY_BRANCH } from './advisory';

export interface PhpVersion {
  raw: string;
  major: number;
  minor: number;
  patch: number;
  prerelease: boolean;
  source: string;
}

const BARE_VERSION = /^(\d+)\.(\d+)\.(\d+)([A-Za-z0-9._-]*)$/;

export function isPrereleaseSuffix(suffix: string): boolean {
  return /^(?:[-_.]?)(?:rc|alpha|beta|dev|preview)\d*/i.test(suffix);
}

export function compareNumeric(
  left: Pick<PhpVersion, 'major' | 'minor' | 'patch'>,
  right: Pick<PhpVersion, 'major' | 'minor' | 'patch'>,
): number {
  if (left.major !== right.major) return left.major - right.major;
  if (left.minor !== right.minor) return left.minor - right.minor;
  return left.patch - right.patch;
}

export function parseVersionToken(
  rawCore: string,
  suffix: string,
  source: string,
): PhpVersion | null {
  const match = BARE_VERSION.exec(`${rawCore}${suffix}`);
  if (!match) return null;
  const suffixText = match[4] ?? '';
  return {
    raw: `${match[1]}.${match[2]}.${match[3]}${suffixText}`.slice(0, 80),
    major: Number(match[1]),
    minor: Number(match[2]),
    patch: Number(match[3]),
    prerelease: isPrereleaseSuffix(suffixText),
    source,
  };
}

export function extractVersionsFromHeader(headerName: string, value: string): PhpVersion[] {
  const source = headerName.toLowerCase();
  const found: PhpVersion[] = [];
  const seen = new Set<string>();
  const versionInPhpToken = /\bPHP\/(\d+)\.(\d+)\.(\d+)([A-Za-z0-9._-]*)/gi;

  for (const match of value.matchAll(versionInPhpToken)) {
    const version = parseVersionToken(
      `${match[1]}.${match[2]}.${match[3]}`,
      match[4] ?? '',
      source,
    );
    if (!version) continue;
    const key = `${version.raw}@${source}`;
    if (seen.has(key)) continue;
    seen.add(key);
    found.push(version);
  }

  if (source === 'x-php-version') {
    const bare = BARE_VERSION.exec(value.trim());
    if (bare) {
      const version = parseVersionToken(
        `${bare[1]}.${bare[2]}.${bare[3]}`,
        bare[4] ?? '',
        source,
      );
      if (version) {
        const key = `${version.raw}@${source}`;
        if (!seen.has(key)) found.push(version);
      }
    }
  }

  return found;
}

export type VersionKind = 'below' | 'eol' | 'at_or_above' | 'prerelease' | 'unlisted';

export interface VersionAssessment {
  kind: VersionKind;
  version: PhpVersion;
  branch: string;
  fixedVersion: string | null;
}

const KIND_PRIORITY: Record<VersionKind, number> = {
  below: 0,
  eol: 1,
  prerelease: 2,
  unlisted: 3,
  at_or_above: 4,
};

export function assessVersion(version: PhpVersion): VersionAssessment {
  const branch = `${version.major}.${version.minor}`;
  const fixed = FIXED_RELEASE_BY_BRANCH.get(branch) ?? null;
  const endOfLife = version.major < 8 || (version.major === 8 && version.minor < 2);

  if (endOfLife) {
    return { kind: 'eol', version, branch, fixedVersion: null };
  }

  if (!fixed) {
    return { kind: 'unlisted', version, branch, fixedVersion: null };
  }

  const fixedParts = fixed.split('.').map(Number);
  const comparison = compareNumeric(version, {
    major: fixedParts[0] ?? 0,
    minor: fixedParts[1] ?? 0,
    patch: fixedParts[2] ?? 0,
  });

  if (version.prerelease) {
    return { kind: 'prerelease', version, branch, fixedVersion: fixed };
  }

  if (comparison < 0) {
    return { kind: 'below', version, branch, fixedVersion: fixed };
  }

  return { kind: 'at_or_above', version, branch, fixedVersion: fixed };
}

export function selectDrivingAssessment(versions: PhpVersion[]): VersionAssessment | null {
  if (versions.length === 0) return null;
  const assessed = versions.map(assessVersion);
  assessed.sort((left, right) => KIND_PRIORITY[left.kind] - KIND_PRIORITY[right.kind]);
  return assessed[0] ?? null;
}
